export const VO_PRESENCE_ALERT_MS = 8000;

export function normalizeVoLeaveKind(kind) {
  const raw = String(kind || "").trim().toLowerCase();
  if (raw === "break" || raw === "exit" || raw === "window_closed") return raw;
  return "other";
}

export function voPresenceAlertCopy(event) {
  const name = String(event?.userName || "").trim() || "Alguém";
  if (event?.type === "enter") {
    return {
      severity: "success",
      title: `${name} entrou no escritório`,
      sound: "enter",
    };
  }
  const leaveKind = normalizeVoLeaveKind(event?.leaveKind);
  if (leaveKind === "break") {
    return {
      severity: "warning",
      title: `${name} saiu para o almoço`,
      sound: "leave_break",
    };
  }
  if (leaveKind === "exit") {
    return {
      severity: "info",
      title: `${name} encerrou o expediente`,
      sound: "leave_exit",
    };
  }
  return {
    severity: "default",
    title: `${name} saiu do escritório`,
    sound: "leave_other",
  };
}

export function presenceEventKey(event) {
  const uid = Number(event?.userId) || 0;
  const roomId = Number(event?.roomId) || 0;
  const leaveKind = event?.type === "leave" ? normalizeVoLeaveKind(event?.leaveKind) : "";
  const instance = String(event?.id || "").trim();
  return `${event?.type || ""}:${uid}:${leaveKind}:${roomId}:${instance}`;
}

export function presenceGenericKey(event) {
  return presenceEventKey({ ...event, id: null });
}

function rememberPresenceKeys(seen, ev) {
  if (!seen || !ev) return "";
  const key = presenceEventKey(ev);
  seen.add(key);
  seen.add(presenceGenericKey(ev));
  return key;
}

/** Marca o evento e diz se ainda não tinha sido anunciado (socket sem id ou poll com id). */
export function notePresenceEvent(seen, ev) {
  if (!seen || !ev) return { isNew: false, key: "" };
  forgetOppositePresenceKeys(seen, ev);
  const key = presenceEventKey(ev);
  const generic = presenceGenericKey(ev);
  const isNew = !seen.has(key) && !seen.has(generic);
  rememberPresenceKeys(seen, ev);
  return { isNew, key };
}

export function parsePresencePayload(res) {
  const envelope = res && typeof res === "object" && !Array.isArray(res) ? res : { data: res };
  const nested =
    envelope.data && typeof envelope.data === "object" && !Array.isArray(envelope.data)
      ? envelope.data
      : envelope;
  const people = Array.isArray(nested.data)
    ? nested.data
    : Array.isArray(nested.people)
      ? nested.people
      : Array.isArray(envelope.data)
        ? envelope.data
        : Array.isArray(envelope.people)
          ? envelope.people
          : Array.isArray(res)
            ? res
            : [];
  const activity = Array.isArray(nested.activity)
    ? nested.activity
    : Array.isArray(envelope.activity)
      ? envelope.activity
      : [];
  const timezone = String(nested.timezone || envelope.timezone || "").trim();
  return { people, activity, timezone };
}

function toEvent(ev, fallbackType) {
  const uid = Number(ev?.userId);
  if (!Number.isFinite(uid) || uid <= 0) return null;
  const type = ev?.type === "leave" || ev?.type === "enter" ? ev.type : fallbackType;
  if (type !== "enter" && type !== "leave") return null;
  const id = String(ev?.id || "").trim();
  return {
    type,
    id: id || null,
    leaveKind: type === "leave" ? normalizeVoLeaveKind(ev.leaveKind) : null,
    userId: uid,
    userName: ev.userName || ev.user_nome || null,
    userPhoto: ev.userPhoto || ev.user_foto || null,
    roomId: Number(ev.roomId || ev.room_id) || 0,
    at: ev.at || ev.joinedAt || ev.left_at || ev.joined_at || null,
  };
}

/**
 * Primeiro snapshot só marca o que já existia. Depois disso, activity (com motivo)
 * ganha prioridade; o diff do piso cobre quem entrou/saiu só no vídeo.
 */
export function collectNewPresenceAlerts({
  prevPeople,
  nextPeople,
  activity = [],
  selfUserId,
  seenKeys,
} = {}) {
  const seen = seenKeys instanceof Set ? seenKeys : new Set();
  const self = Number(selfUserId) || 0;
  const fromActivity = [];
  for (const raw of activity || []) {
    const ev = toEvent(raw);
    if (!ev || ev.userId === self) continue;
    fromActivity.push(ev);
  }

  if (prevPeople == null) {
    for (const ev of fromActivity) rememberPresenceKeys(seen, ev);
    for (const p of nextPeople || []) {
      const ev = toEvent({ ...p, type: "enter", at: p.joinedAt });
      if (ev) rememberPresenceKeys(seen, ev);
    }
    return { alerts: [], seenKeys: seen };
  }

  const prevIds = new Set(
    (prevPeople || []).map((p) => Number(p.userId)).filter((n) => Number.isFinite(n) && n > 0)
  );
  const nextIds = new Set(
    (nextPeople || []).map((p) => Number(p.userId)).filter((n) => Number.isFinite(n) && n > 0)
  );
  const covered = new Set(fromActivity.map((ev) => `${ev.type}:${ev.userId}`));

  for (const p of nextPeople || []) {
    const uid = Number(p.userId);
    if (!uid || uid === self || prevIds.has(uid) || covered.has(`enter:${uid}`)) continue;
    const ev = toEvent({ ...p, type: "enter", at: p.joinedAt || new Date().toISOString() });
    if (ev) fromActivity.push(ev);
  }
  for (const p of prevPeople || []) {
    const uid = Number(p.userId);
    if (!uid || uid === self || nextIds.has(uid) || covered.has(`leave:${uid}`)) continue;
    const ev = toEvent({
      ...p,
      type: "leave",
      leaveKind: "other",
      at: new Date().toISOString(),
    });
    if (ev) fromActivity.push(ev);
  }

  const alerts = [];
  for (const ev of fromActivity) {
    const present = nextIds.has(ev.userId);
    if (ev.type === "leave" && present) {
      rememberPresenceKeys(seen, ev);
      continue;
    }
    const { isNew, key } = notePresenceEvent(seen, ev);
    if (!isNew) continue;
    alerts.push({ ...ev, key, ...voPresenceAlertCopy(ev) });
  }
  return { alerts, seenKeys: seen };
}

/** Só apaga a chave genérica (socket/diff). Chaves com id de sessão continuam vistas. */
export function forgetOppositePresenceKeys(seen, ev) {
  if (!seen || typeof seen.delete !== "function") return;
  const uid = Number(ev?.userId) || 0;
  const roomId = Number(ev?.roomId) || 0;
  if (ev?.type === "leave") {
    seen.delete(`enter:${uid}::${roomId}:`);
    seen.delete(`enter:${uid}::${roomId}`);
    return;
  }
  if (ev?.type === "enter") {
    for (const kind of ["break", "exit", "window_closed", "other"]) {
      seen.delete(`leave:${uid}:${kind}:${roomId}:`);
      seen.delete(`leave:${uid}:${kind}:${roomId}`);
    }
  }
}
